/**
 * Personaleinsatz – zentrale Erfassung der Mitarbeiter-Meldungen je übergeordneter
 * Maßnahme (Vergabe). Haupt-Arbeitsblatt mit allen Zuordnungen, Filter nach Vergabe,
 * Excel-Export (Gesamtübersicht + Blatt je Vergabe). "Einsatz in weiteren Maßnahmen"
 * wird automatisch aus den anderen Zuordnungen abgeleitet.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MaybeLayout } from '../components/layout/MaybeLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ClipboardList, Plus, Pencil, Trash2, Download, Loader2, Users, MapPin, AlertTriangle, ChevronDown, Building2 } from 'lucide-react';
import { personaleinsatzAPI, masterMeasuresAPI, employeesAPI, locationsAPI, branchesAPI, companyAPI } from '../lib/api';
import { OrgUnitFilter, orgUnitToLegacyParams, flattenOrgTree } from '../components/OrgUnitFilter';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from '../components/ui/command';
import { formatLastFirst, getDuplicateNameKeys } from '../lib/nameFormat';
import { useOrgLabels } from '../contexts/OrgLabelsContext';
import { toast } from 'sonner';

const EMPTY = { employee_id: '', master_measure_id: '', einsatz_als: '', qualifikation: '', std_wo: '' };

// Inline-bearbeitbare Zelle: Klick -> Eingabefeld -> Enter/Blur speichert sofort.
// onSave(newValue) muss true (Erfolg) oder false (Fehler -> zurücksetzen) liefern.
const InlineEditCell = ({ value, type = 'text', onSave, testid, placeholder = '–', inputClass = 'h-8 w-24' }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(value ?? ''); }, [value]);

  const commit = async () => {
    if (saving) return;
    const newVal = type === 'number' ? (String(val).trim() === '' ? null : Number(val)) : String(val).trim();
    const orig = type === 'number' ? (value ?? null) : (value ?? '');
    if (newVal === orig) { setEditing(false); return; }
    setSaving(true);
    const ok = await onSave(newVal);
    setSaving(false);
    if (ok) setEditing(false);
    else setVal(value ?? '');
  };

  if (!editing) {
    const display = (value ?? '') === '' ? placeholder : value;
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        data-testid={testid}
        className="group inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-secondary/60 transition-colors"
        title="Zum Bearbeiten klicken"
      >
        <span>{display}</span>
        <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-50" />
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <Input
        autoFocus
        type={type}
        step={type === 'number' ? '0.5' : undefined}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setVal(value ?? ''); setEditing(false); } }}
        onBlur={commit}
        disabled={saving}
        data-testid={`${testid}-input`}
        className={inputClass}
      />
      {saving && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
    </span>
  );
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(String(iso).slice(0, 10)).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch { return iso; }
};

export default function Personaleinsatz() {
  const orgL = useOrgLabels();
  const [rows, setRows] = useState([]);
  const [measures, setMeasures] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMeasure, setFilterMeasure] = useState('all');
  const [filterOrgUnit, setFilterOrgUnit] = useState('all');
  const [orgTree, setOrgTree] = useState([]);
  const [orgLevels, setOrgLevels] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  // OrgUnit-Auswahl → Backend-Parameter (Standort/Niederlassung/Firmenweit).
  const peParamsFor = (v) => {
    if (!v || v === 'all') return {};
    if (v === '__none__') return { location_id: '__none__' };
    return orgUnitToLegacyParams(orgTree, v);
  };

  // Export auslösen und Popover zuverlässig schließen (nach Command-onSelect).
  const runExport = (params) => {
    doExport(params);
    setTimeout(() => setExportOpen(false), 30);
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [suggestion, setSuggestion] = useState({ positions: [], qualifikationen: [] });
  const [deleteRow, setDeleteRow] = useState(null);

  // Scroll-Container: Höhe dynamisch an tatsächliche Position koppeln, damit die
  // horizontale Scrollleiste immer im Sichtfenster bleibt (nicht erst nach dem
  // Herunterscrollen der Seite erreichbar) und die letzten Spalten sichtbar sind.
  const tableScrollRef = useRef(null);
  const [tableMaxHeight, setTableMaxHeight] = useState(null);
  useEffect(() => {
    const recompute = () => {
      const el = tableScrollRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const available = Math.round(window.innerHeight - top - 24);
      setTableMaxHeight((prev) => {
        const next = Math.max(320, available);
        return prev === next ? prev : next;
      });
    };
    recompute();
    const id = window.setTimeout(recompute, 150);
    window.addEventListener('resize', recompute);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('resize', recompute);
    };
  }, [loading, rows, filterMeasure, filterOrgUnit]);

  const load = async () => {
    try {
      setLoading(true);
      const peParams = peParamsFor(filterOrgUnit);
      const [pRes, mRes, eRes, lRes, bRes, tRes] = await Promise.all([
        personaleinsatzAPI.getAll(peParams),
        masterMeasuresAPI.getAll(),
        employeesAPI.getAll({ is_active: true, taetigkeitsart: 'operativ' }),
        locationsAPI.getAll({ is_active: true }),
        branchesAPI.getAll({ is_active: true }),
        companyAPI.getOrgTree().catch(() => ({ data: { tree: [], levels: [] } })),
      ]);
      setRows(pRes.data || []);
      setMeasures(mRes.data || []);
      setEmployees(eRes.data || []);
      setLocations(lRes.data || []);
      setBranches(bRes.data || []);
      setOrgTree(tRes.data?.tree || []);
      setOrgLevels(tRes.data?.levels || []);
    } catch {
      toast.error('Fehler beim Laden der Personaleinsatz-Daten');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterOrgUnit]); // eslint-disable-line react-hooks/exhaustive-deps

  // Leichtes Neuladen der Zeilen (ohne Spinner-Flackern) nach Inline-Bearbeitung.
  const refreshRows = async () => {
    const peParams = peParamsFor(filterOrgUnit);
    const pRes = await personaleinsatzAPI.getAll(peParams);
    const data = pRes.data || [];
    setRows(data);
    return data;
  };

  // Warn-Toast bei Überplanung (verplant > Vertragsstunden) anhand frischer Daten.
  const warnIfOverplanned = (data, employeeId) => {
    const row = (data || []).find((r) => r.employee_id === employeeId);
    if (row && row.weekly_hours != null && (row.gesamt_std_wo || 0) > row.weekly_hours) {
      toast.warning(
        `Achtung: ${row.gesamt_std_wo}/${row.weekly_hours} Std. – ${row.last_name || 'Mitarbeiter'} ist über Vertrag verplant`,
        { duration: 6000 },
      );
    }
  };

  // Inline-Speichern eines einzelnen Feldes (std_wo oder einsatz_als).
  const saveInline = async (row, field, newVal) => {
    if (field === 'einsatz_als') {
      const fn = (newVal || '').trim();
      const others = rows
        .filter((r) => r.employee_id === row.employee_id && r.master_measure_id === row.master_measure_id && r.id !== row.id)
        .map((r) => (r.einsatz_als || '').trim().toLowerCase());
      if (others.length > 0 && !fn) {
        toast.error('Dieser Mitarbeiter ist der Maßnahme mehrfach zugeordnet – Funktion ist Pflicht.');
        return false;
      }
      if (others.includes(fn.toLowerCase())) {
        toast.error('Diese Funktion ist für den Mitarbeiter in dieser Maßnahme bereits vergeben.');
        return false;
      }
    }
    try {
      await personaleinsatzAPI.update(row.id, { [field]: newVal });
      const fresh = await refreshRows();
      toast.success('Gespeichert');
      warnIfOverplanned(fresh, row.employee_id);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler beim Speichern');
      return false;
    }
  };

  const filteredRows = useMemo(() => (
    filterMeasure === 'all' ? rows : rows.filter((r) => r.master_measure_id === filterMeasure)
  ), [rows, filterMeasure]);

  const FIRMENWEIT = 'Firmenweit / ohne Standort';
  // Nach Standort gruppieren – eine Meldung erscheint unter JEDEM Standort ihrer
  // Maßnahme; Maßnahmen ohne Standort unter "Firmenweit / ohne Standort".
  const groupedByStandort = useMemo(() => {
    const groups = {};
    filteredRows.forEach((r) => {
      const labels = (r.location_names && r.location_names.length) ? r.location_names : [FIRMENWEIT];
      labels.forEach((lab) => { (groups[lab] = groups[lab] || []).push(r); });
    });
    return Object.keys(groups)
      .sort((a, b) => (a === FIRMENWEIT ? 1 : 0) - (b === FIRMENWEIT ? 1 : 0) || a.localeCompare(b))
      .map((lab) => ({
        label: lab,
        rows: groups[lab],
        gesamt: groups[lab].reduce((s, r) => s + (r.std_wo || 0), 0),
        personen: new Set(groups[lab].map((r) => r.employee_id)).size,
      }));
  }, [filteredRows]);

  // Maßnahme-Spalte nur anzeigen, wenn keine konkrete Maßnahme gefiltert ist
  const showMeasureCol = filterMeasure === 'all';

  const openCreate = () => {
    setEditId(null);
    // Aktuell gefilterte Maßnahme direkt vorbelegen (spart die erneute Auswahl).
    setForm(filterMeasure && filterMeasure !== 'all'
      ? { ...EMPTY, master_measure_id: filterMeasure }
      : EMPTY);
    setSuggestion({ positions: [], qualifikationen: [] });
    setDialogOpen(true);
  };

  const openEdit = (r) => {
    setEditId(r.id);
    setForm({
      employee_id: r.employee_id,
      master_measure_id: r.master_measure_id,
      einsatz_als: r.einsatz_als || '',
      qualifikation: r.qualifikation || '',
      std_wo: r.std_wo ?? '',
    });
    loadSuggestion(r.employee_id, false);
    setDialogOpen(true);
  };

  const loadSuggestion = async (employeeId, prefill = true) => {
    if (!employeeId) { setSuggestion({ positions: [], qualifikationen: [] }); return; }
    try {
      const res = await personaleinsatzAPI.suggestion(employeeId);
      setSuggestion(res.data || { positions: [], qualifikationen: [] });
      if (prefill) {
        // Bereits in dieser Maßnahme vergebene Funktionen dieses Mitarbeiters
        // nicht erneut vorschlagen (verhindert versehentliche Duplikate).
        const taken = new Set(
          rows
            .filter((r) => r.employee_id === employeeId && r.master_measure_id === form.master_measure_id)
            .map((r) => (r.einsatz_als || '').trim().toLowerCase())
        );
        const freiePosition = (res.data.positions || []).find((p) => !taken.has((p || '').trim().toLowerCase())) || '';
        setForm((f) => ({
          ...f,
          einsatz_als: f.einsatz_als || freiePosition,
          qualifikation: f.qualifikation || (res.data.qualifikation_text || ''),
        }));
      }
    } catch { /* ignore */ }
  };

  const onEmployeeChange = (employeeId) => {
    setForm((f) => ({ ...f, employee_id: employeeId, einsatz_als: '', qualifikation: '' }));
    loadSuggestion(employeeId, true);
  };

  const toggleQual = (name) => {
    const current = (form.qualifikation || '').split(',').map((s) => s.trim()).filter(Boolean);
    const exists = current.includes(name);
    const next = exists ? current.filter((q) => q !== name) : [...current, name];
    setForm((f) => ({ ...f, qualifikation: next.join(', ') }));
  };

  const save = async () => {
    if (!form.employee_id || !form.master_measure_id) {
      toast.error('Bitte Mitarbeiter und Maßnahme wählen');
      return;
    }
    const funktion = (form.einsatz_als || '').trim();
    if (existingFunctions.length > 0 && !funktion) {
      toast.error('Dieser Mitarbeiter ist der Maßnahme bereits zugeordnet – bitte eine Funktion („Einsatz als") angeben.');
      return;
    }
    if (existingFunctions.some((f) => f.toLowerCase() === funktion.toLowerCase())) {
      toast.error('Dieser Mitarbeiter ist der Maßnahme in dieser Funktion bereits zugeordnet.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        std_wo: form.std_wo === '' || form.std_wo === null ? null : parseFloat(form.std_wo),
      };
      if (editId) {
        const { employee_id, master_measure_id, ...upd } = payload;
        await personaleinsatzAPI.update(editId, upd);
      } else {
        await personaleinsatzAPI.create(payload);
      }
      toast.success('Personaleinsatz gespeichert');
      setDialogOpen(false);
      const fresh = await refreshRows();
      warnIfOverplanned(fresh, form.employee_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteRow) return;
    try {
      await personaleinsatzAPI.delete(deleteRow.id);
      toast.success('Eintrag entfernt');
      setDeleteRow(null);
      load();
    } catch {
      toast.error('Fehler beim Entfernen');
    }
  };

  const doExport = async (opts = {}) => {
    setExporting(true);
    try {
      // opts: { location_id } | { branch_id } | { location_id: '__none__' } | {} (alle)
      const params = {};
      let labelSuffix = '';
      if (opts.location_id === '__none__') {
        params.location_id = '__none__';
        labelSuffix = '_Firmenweit';
      } else if (opts.location_id) {
        params.location_id = opts.location_id;
        labelSuffix = `_${(locations.find((l) => l.id === opts.location_id)?.name || 'Standort').replace(/[^A-Za-z0-9_-]+/g, '_')}`;
      } else if (opts.branch_id) {
        params.branch_id = opts.branch_id;
        labelSuffix = `_NL-${(branches.find((b) => b.id === opts.branch_id)?.name || 'Niederlassung').replace(/[^A-Za-z0-9_-]+/g, '_')}`;
      }
      const res = await personaleinsatzAPI.export(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Personaleinsatz_Uebersicht${labelSuffix}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export erstellt');
    } catch {
      toast.error('Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  // Alle aktiven Mitarbeiter sind wählbar. Derselbe Mitarbeiter darf einer
  // Maßnahme mehrfach (in verschiedenen Funktionen) gemeldet werden.
  const availableEmployees = employees;
  const empDupKeys = useMemo(() => getDuplicateNameKeys(employees), [employees]);

  // Bereits gemeldete Funktionen des gewählten Mitarbeiters in dieser Maßnahme
  // (zur Unterscheidung + Pflicht der Funktion bei Mehrfacheinsatz).
  const existingFunctions = useMemo(() => {
    if (!form.employee_id || !form.master_measure_id) return [];
    return rows
      .filter((r) => r.employee_id === form.employee_id
        && r.master_measure_id === form.master_measure_id
        && r.id !== editId)
      .map((r) => (r.einsatz_als || '').trim())
      .filter(Boolean);
  }, [rows, form.employee_id, form.master_measure_id, editId]);

  return (
    <MaybeLayout>
      <div className="space-y-6" data-testid="personaleinsatz-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
              <ClipboardList className="h-6 w-6 text-primary" />
              Personaleinsatz
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Mitarbeiter-Meldungen je übergeordneter Maßnahme – inkl. automatischer Übersicht „Einsatz in weiteren Maßnahmen" und Excel-Export für den Auftraggeber.
            </p>
          </div>
          <div className="flex gap-2">
            <Popover open={exportOpen} onOpenChange={setExportOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="rounded-full" disabled={exporting} data-testid="export-personaleinsatz-btn">
                  {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                  Excel-Export
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-0">
                <Command>
                  <CommandInput placeholder={`${orgL.location}/${orgL.branch} suchen…`} data-testid="export-search" />
                  <CommandList className="max-h-[60vh]">
                    <CommandEmpty>Keine Einheit gefunden.</CommandEmpty>
                    <CommandGroup heading="Schnellauswahl">
                      <CommandItem value="gesamt alle" onSelect={() => runExport({})} data-testid="export-all">
                        <ClipboardList className="h-4 w-4 mr-2" /> Gesamtübersicht (alle)
                      </CommandItem>
                      {filterOrgUnit && filterOrgUnit !== 'all' && filterOrgUnit !== '__none__' && (
                        <CommandItem value="aktuelle auswahl filter" onSelect={() => runExport(peParamsFor(filterOrgUnit))} data-testid="export-current-filter">
                          <MapPin className="h-4 w-4 mr-2" /> Aktuelle Auswahl
                        </CommandItem>
                      )}
                      <CommandItem value="firmenweit ohne standort" onSelect={() => runExport({ location_id: '__none__' })} data-testid="export-firmenweit">
                        <Building2 className="h-4 w-4 mr-2" /> Nur firmenweite Maßnahmen
                      </CommandItem>
                    </CommandGroup>
                    {orgTree.length > 0 && (
                      <CommandGroup heading="Organisationseinheiten">
                        {flattenOrgTree(orgTree, orgLevels).map((u) => (
                          <CommandItem
                            key={u.id}
                            value={`${u.name} ${u.levelLabel}`}
                            onSelect={() => runExport(peParamsFor(u.id))}
                            data-testid={`export-unit-${u.id}`}
                          >
                            {u.id.startsWith('ou-branch-')
                              ? <Building2 className="h-4 w-4 mr-2 text-muted-foreground" />
                              : <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />}
                            <span style={{ paddingLeft: `${u.depth * 12}px` }}>{u.name}</span>
                            {u.levelLabel && <span className="ml-1 text-xs text-muted-foreground">· {u.levelLabel}</span>}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <Button className="rounded-full" onClick={openCreate} data-testid="add-personaleinsatz-btn">
              <Plus className="h-4 w-4 mr-2" /> Meldung hinzufügen
            </Button>
          </div>
        </div>

        <Card className="border-border/60">
          <CardHeader className="border-b border-border/40 pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <CardTitle className="font-heading text-lg">Haupt-Arbeitsblatt</CardTitle>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <div className="w-full sm:w-56">
                  <OrgUnitFilter
                    tree={orgTree}
                    levels={orgLevels}
                    value={filterOrgUnit}
                    onChange={setFilterOrgUnit}
                    showLabel={false}
                    testId="filter-org-unit"
                    extraOptions={[{ value: '__none__', label: `Firmenweit / ohne ${orgL.location}` }]}
                  />
                </div>
                <div className="w-full sm:w-72">
                  <Select value={filterMeasure} onValueChange={setFilterMeasure}>
                    <SelectTrigger data-testid="filter-measure-select"><SelectValue placeholder="Maßnahme filtern" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Maßnahmen</SelectItem>
                      {measures.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.name}{m.vergabenummer ? ` (${m.vergabenummer})` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
            ) : filteredRows.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground" data-testid="personaleinsatz-empty">
                <Users className="h-10 w-10 mx-auto mb-2 opacity-40" />
                <p>Noch keine Meldungen erfasst.</p>
                <Button variant="link" onClick={openCreate}>Erste Meldung hinzufügen</Button>
              </div>
            ) : (
              <div
                ref={tableScrollRef}
                className="overflow-auto"
                style={{ maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : undefined }}
              >
                <Table wrapperClassName="overflow-visible">
                  <TableHeader className="sticky top-0 z-10 bg-background">
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Vorname</TableHead>
                      <TableHead className="hidden md:table-cell">Geburtsdatum</TableHead>
                      {showMeasureCol && <TableHead>Maßnahme</TableHead>}
                      <TableHead>Einsatz als</TableHead>
                      <TableHead className="hidden lg:table-cell">Qualifikation</TableHead>
                      <TableHead className="text-right">Std./Wo.</TableHead>
                      <TableHead className="hidden lg:table-cell">Einsatz in weiteren Maßnahmen</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupedByStandort.map((group) => (
                      <React.Fragment key={group.label}>
                        <TableRow className="bg-secondary/40 hover:bg-secondary/40" data-testid={`standort-group-${group.label}`}>
                          <TableCell colSpan={showMeasureCol ? 9 : 8} className="font-heading font-semibold text-sm py-2">
                            <span className="inline-flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-primary" />
                              {group.label}
                              <Badge variant="outline" className="text-xs">{group.rows.length} Meldung(en)</Badge>
                              <Badge variant="outline" className="text-xs" data-testid={`standort-group-personen-${group.label}`}>{group.personen} {group.personen === 1 ? 'Person' : 'Personen'}</Badge>
                              <span className="text-xs text-muted-foreground font-normal">Σ {group.gesamt} Std./Wo.</span>
                            </span>
                          </TableCell>
                        </TableRow>
                        {group.rows.map((r) => {
                          const verplant = r.gesamt_std_wo ?? 0;
                          const vertrag = r.weekly_hours;
                          const over = vertrag != null && verplant > vertrag;
                          return (
                          <TableRow key={`${group.label}-${r.id}`} data-testid={`personaleinsatz-row-${r.id}`}>
                            <TableCell className="font-medium">
                              <div>{r.last_name}</div>
                              <div
                                className={`text-xs inline-flex items-center gap-1 mt-0.5 ${over ? 'text-destructive font-medium' : 'text-muted-foreground'}`}
                                data-testid={`auslastung-${r.id}`}
                                title={over
                                  ? `Überplanung: ${verplant} Std./Wo. verplant, Vertrag ${vertrag} Std./Wo.`
                                  : `Über alle Maßnahmen verplant: ${verplant} Std./Wo.${vertrag != null ? ` (Vertrag ${vertrag})` : ''}`}
                              >
                                {over && <AlertTriangle className="h-3 w-3" />}
                                verplant {verplant} / Vertrag {vertrag != null ? vertrag : '–'} Std.
                              </div>
                            </TableCell>
                            <TableCell>{r.first_name}</TableCell>
                            <TableCell className="hidden md:table-cell">{fmtDate(r.date_of_birth)}</TableCell>
                            {showMeasureCol && (
                              <TableCell>
                                <span className="text-sm">{r.measure_name}</span>
                                {r.vergabenummer && <div className="text-xs text-muted-foreground">{r.vergabenummer}{r.los_nr ? ` / ${r.los_nr}` : ''}</div>}
                              </TableCell>
                            )}
                            <TableCell>
                              <InlineEditCell
                                value={r.einsatz_als || ''}
                                onSave={(v) => saveInline(r, 'einsatz_als', v)}
                                testid={`inline-einsatz-${r.id}`}
                                placeholder="–"
                                inputClass="h-8 w-40"
                              />
                            </TableCell>
                            <TableCell className="hidden lg:table-cell text-sm text-muted-foreground max-w-[220px] truncate" title={r.qualifikation || ''}>{r.qualifikation || '-'}</TableCell>
                            <TableCell className="text-right">
                              <InlineEditCell
                                value={r.std_wo ?? ''}
                                type="number"
                                onSave={(v) => saveInline(r, 'std_wo', v)}
                                testid={`inline-stdwo-${r.id}`}
                                placeholder="–"
                                inputClass="h-8 w-20"
                              />
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              {r.weitere_massnahmen?.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {r.weitere_massnahmen.map((w, i) => (
                                    <Badge key={i} variant="secondary" className="text-xs" title={w.measure_name || w.vergabe_los}>
                                      {w.vergabe_los}{w.std_wo != null ? ` · ${w.std_wo} Std.` : ''}
                                    </Badge>
                                  ))}
                                </div>
                              ) : <span className="text-xs text-muted-foreground">–</span>}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              <Button size="icon" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-personaleinsatz-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                              <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDeleteRow(r)} data-testid={`delete-personaleinsatz-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="personaleinsatz-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? 'Meldung bearbeiten' : 'Meldung hinzufügen'}</DialogTitle>
            <DialogDescription className="sr-only">Mitarbeiter einer übergeordneten Maßnahme zuordnen und Einsatzdaten erfassen.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Übergeordnete Maßnahme (Vergabe)</Label>
              <Select value={form.master_measure_id} onValueChange={(v) => setForm((f) => ({ ...f, master_measure_id: v }))} disabled={!!editId}>
                <SelectTrigger data-testid="pe-measure-select"><SelectValue placeholder="Maßnahme wählen" /></SelectTrigger>
                <SelectContent>
                  {measures.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name}{m.vergabenummer ? ` (${m.vergabenummer})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Mitarbeiter</Label>
              <Select value={form.employee_id} onValueChange={onEmployeeChange} disabled={!!editId}>
                <SelectTrigger data-testid="pe-employee-select"><SelectValue placeholder="Mitarbeiter wählen" /></SelectTrigger>
                <SelectContent>
                  {availableEmployees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{formatLastFirst(e, empDupKeys)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Einsatz als</Label>
              {suggestion.positions?.length > 0 ? (
                <Select value={form.einsatz_als} onValueChange={(v) => setForm((f) => ({ ...f, einsatz_als: v }))}>
                  <SelectTrigger data-testid="pe-einsatz-select"><SelectValue placeholder="Funktion wählen" /></SelectTrigger>
                  <SelectContent>
                    {suggestion.positions.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={form.einsatz_als} onChange={(e) => setForm((f) => ({ ...f, einsatz_als: e.target.value }))} placeholder="z. B. Sozialpädagoge, Lehrkraft, Job-Coach" data-testid="pe-einsatz-input" />
              )}
              {existingFunctions.length > 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400" data-testid="pe-existing-functions">
                  Bereits in dieser Maßnahme gemeldet als: {existingFunctions.join(', ')}. Für eine weitere Meldung bitte eine andere Funktion wählen.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Qualifikation für vorgesehenen Einsatz</Label>
              <Input value={form.qualifikation} onChange={(e) => setForm((f) => ({ ...f, qualifikation: e.target.value }))} placeholder="Qualifikation(en)" data-testid="pe-qualifikation-input" />
              {suggestion.qualifikationen?.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {suggestion.qualifikationen.map((q) => {
                    const active = (form.qualifikation || '').split(',').map((s) => s.trim()).includes(q);
                    return (
                      <button
                        type="button"
                        key={q}
                        onClick={() => toggleQual(q)}
                        className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${active ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-secondary'}`}
                        data-testid={`pe-qual-chip-${q}`}
                      >
                        {active ? '✓ ' : '+ '}{q}
                      </button>
                    );
                  })}
                </div>
              )}
              <p className="text-xs text-muted-foreground">Vorschläge aus „Qualifikationen" des Mitarbeiters – anklickbar, frei anpassbar.</p>
            </div>
            <div className="space-y-2">
              <Label>Std./Wo. (in dieser Maßnahme)</Label>
              <Input type="number" min="0" step="0.5" value={form.std_wo} onChange={(e) => setForm((f) => ({ ...f, std_wo: e.target.value }))} placeholder="z. B. 20" data-testid="pe-stdwo-input" className="max-w-[160px]" />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" className="rounded-full" onClick={() => setDialogOpen(false)} disabled={saving}>Abbrechen</Button>
              <Button className="rounded-full" onClick={save} disabled={saving} data-testid="save-personaleinsatz-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Speichern'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteRow} onOpenChange={(o) => { if (!o) setDeleteRow(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Meldung entfernen?</AlertDialogTitle>
            <AlertDialogDescription>
              Die Zuordnung von {deleteRow?.first_name} {deleteRow?.last_name} zu „{deleteRow?.measure_name}" wird entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete-personaleinsatz">Entfernen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MaybeLayout>
  );
}
